<script setup lang="ts">
const router = useRouter();
const route = useRoute();
import { useSettings } from "~/store/settings";
import { IEvent } from "~/types/racing/event";
import api from "~/utils/auth/api";
import { useQuery } from "@tanstack/vue-query";
import LeagueAccordion from "~/components/layout/TrackRacing/Shared/Event/LeagueAccordion.vue";
import RacingEvent from "~/components/layout/TrackRacing/Shared/Event/RacingEvent.vue";
import SpecialEvents from "../SpecialEvents/SpecialEvents.vue";
import t from "~/utils/translate";
import Loader from "./Loader.vue";

const settings = useSettings();

export interface IAntePostEvents {
	name: string;
	events: IEvent[];
}
export interface ILeagues {
	leagues: IAntePostEvents[];
}
interface Props {
	sportId: string | undefined;
	selectedNav: string | undefined;
	isSpecialTab?: boolean | undefined;
}
const props = defineProps<Props>();
const selectedEventId = computed(() => {
	return route?.query?.eventId;
});

const eventType = computed(() => {
	return route?.query?.eventType?.toString().toLowerCase();
});

const antePostApiCall = () => {
	return api<ILeagues>({
		url: `/TrackRacing/Events/AntePost/TrackRacing?countryCode=${settings.config?.appSetting?.CountryCode}&sportId=${props.sportId}`,
		type: "horses",
		apiVersion: "v1",
		options: { method: "GET" },
	}).then(async (response) => {
		const outrightEvents = response?.leagues
			.map((league) => ({
				...league,
				events:
					!!league.events &&
					league.events.filter(
						(event) =>
							(event.sportSpecificProperties.isFinalDeclarations !== "True" && props.selectedNav === "antepost" && !props.isSpecialTab) ||
							(event.sportSpecificProperties.isFinalDeclarations === "True" && props.selectedNav === "future" && !props.isSpecialTab) ||
							(props.isSpecialTab && event.league.toLowerCase() === eventType.value)
					),
			}))
			.filter(
				(league) =>
					(!props.isSpecialTab && league.events.length > 0) ||
					(props.isSpecialTab && eventType.value && league.name.toLowerCase() === eventType.value)
			);
		return { leagues: outrightEvents };
	});
};
const {
	data: antePostEvents,
	isLoading,
	isFetching,
	refetch: refreshOutrightEvents,
} = useQuery<ILeagues>({
	queryKey: [`ante-post-events-${props.isSpecialTab ? "special-event" : eventType.value}`],
	queryFn: () => antePostApiCall(),
});

watch(
	() => eventType.value,
	(newEventType, oldEventType) => {
		if (newEventType !== oldEventType) {
			refreshOutrightEvents();
		}
	}
);
</script>
<template>
	<div v-if="!isFetching && !isLoading" class="mt-1">
		<RacingEvent
			v-if="selectedEventId"
			:selected-event-id="+selectedEventId"
			:show-related-events-nav="false"
			:is-special-event="isSpecialTab"
			:is-outright="isSpecialTab ? false : true"
			:dailyRaceEvents="antePostEvents?.leagues[0]?.events || undefined"
		/>
		<div v-else>
			<div v-if="antePostEvents && antePostEvents?.leagues.length > 0">
				<SpecialEvents v-if="isSpecialTab && !isLoading" :special-events="antePostEvents.leagues" />
				<div v-else>
					<LeagueAccordion
						v-for="league in antePostEvents?.leagues"
						:regionLabel="league.name"
						:region-id="league.events[0].regionId"
						:default-value="true"
						:showRegionFlag="true"
						class="rounded-lg bg-light-300 dark:bg-dark-800 my-2"
					>
						<div class="p-2">
							<div class="grid grid-cols-1 md:grid-cols-3 gap-2">
								<div
									v-for="event in league.events"
									@click="router.push({ query: { ...route.query, eventId: event.eventId } })"
									class="min-h-[42px] bg-light-50 dark:bg-dark-700 flex items-center p-2 text-xs w-full truncate rounded-md font-bold text-dark-400 dark:text-light-50 cursor-pointer"
								>
									{{ event.displayName }}
								</div>
							</div>
						</div>
					</LeagueAccordion>
				</div>
			</div>
			<div v-else class="w-full rounded-lg bg-light-50 dark:bg-dark-800 text-xs text-center font-bold my-2 py-2">
				{{ t("no-events-available") }}
			</div>
		</div>
	</div>
	<Loader v-else />
</template>
